// CASEY AI Investor Deck Data

export const companyInfo = {
  name: "CASEY AI",
  tagline: "The Clarity Infrastructure for Career Decision-Making",
  description: "An AI career coach that actually understands you. Talk about your background, choose a role, and follow a personalized roadmap to get job-ready.",
  website: "https://www.caseyai.co",
  email: "team@caseyai.co",
  linkedin: "https://www.linkedin.com/company/caseyai",
  calendly: "https://calendly.com/team-caseyai/new-meeting",
  newsletter: "https://newsletter.caseyai.co/",
  headquarters: "San Francisco, California",
  founded: "2025",
  logo: "https://framerusercontent.com/images/vRMyI2tO87wlq4wLTpu9EQIaJ4U.svg?width=856&height=227",
  acronym: [
    { letter: "C", word: "Clarity" },
    { letter: "A", word: "Alignment" },
    { letter: "S", word: "Support" },
    { letter: "E", word: "Empowerment" },
    { letter: "Y", word: "You" }
  ]
};

export const problemStats = [
  {
    id: 1,
    value: "2,000+",
    label: "Students per career coach",
    sublabel: "Industry average",
    icon: "users"
  },
  {
    id: 2,
    value: "87%",
    label: "Gen Z workers",
    sublabel: "Feel unprepared for careers",
    icon: "alert"
  },
  {
    id: 3,
    value: "60%+",
    label: "Companies struggle",
    sublabel: "Finding candidates with right fit",
    icon: "building"
  },
  {
    id: 4,
    value: "40%",
    label: "New graduates leave",
    sublabel: "Within their first year",
    icon: "trending-down"
  }
];

export const problemDetails = [
  {
    title: "People lack career clarity",
    description: "People make career decisions with guesswork.",
    points: [
      "60%+ of adults don't understand which roles fit their strengths and values",
      "50%+ expect to change careers due to shifting industries",
      "Private coaching costs $125-$500/hour, inaccessible to 85% of households"
    ]
  },
  {
    title: "Companies lack hiring signal",
    description: "Companies hire with limited signal beyond resumes.",
    points: [
      "$310B+ spent annually on recruiting in the US",
      "Average cost per hire exceeds $4,700",
      "75%+ report difficulty finding candidates with right skills and fit"
    ]
  },
  {
    title: "Tools manage resumes, not people",
    description: "Most tools store history, not potential.",
    points: [
      "Job boards optimize for scale, not clarity",
      "Assessments are static and generic with no market integration",
      "University tools have <20% engagement rates"
    ]
  }
];

export const solutionModules = [
  {
    id: 1,
    title: "Cognitive Profiling",
    description: "Builds a dynamic, high-resolution profile by analyzing behavioral patterns, motivations, interests, and communication style.",
    features: [
      "Evolving model of identity & preferences",
      "Accurate detection of cognitive patterns",
      "Insight into role fit & working style"
    ],
    icon: "brain"
  },
  {
    id: 2,
    title: "Adaptive Coaching Conversations",
    description: "Natural, ongoing dialogue to uncover identity, values, strengths, fears, motivations, and long-term goals.",
    features: [
      "Adjusts to emotional & cognitive patterns",
      "Responds to user motivations & challenges",
      "Provides structure for decision-making"
    ],
    icon: "messages"
  },
  {
    id: 3,
    title: "Personalized Career Roadmaps",
    description: "Structured, adaptive roadmaps guiding users from self-awareness to opportunity alignment.",
    features: [
      "Decision frameworks & milestones",
      "Recommended skills & courses",
      "Long-term growth pathways"
    ],
    icon: "map"
  },
  {
    id: 4,
    title: "Opportunity Matching",
    description: "Combines identity insight with real-time labor intelligence to recommend aligned opportunities.",
    features: [
      "Courses & certifications from vetted partners",
      "Skills-based learning paths",
      "Emerging roles suited for the user"
    ],
    icon: "target"
  }
];

export const marketData = {
  totalMarket: "$427B+",
  marketNote: "US Market Size",
  segments: [
    {
      name: "Talent Recruitment",
      value: 310,
      description: "US employers invest over $310B annually in recruiting",
      color: "#0EA5E9"
    },
    {
      name: "Professional Learning",
      value: 95,
      description: "US upskilling & corporate training market at $95B+",
      color: "#10B981"
    },
    {
      name: "Coaching & Guidance",
      value: 20,
      description: "US career coaching and guidance industry at $20B+",
      color: "#8B5CF6"
    },
    {
      name: "Higher Education",
      value: 2,
      description: "US universities allocate $2B+ to career services annually",
      color: "#F59E0B"
    }
  ],
  targetAudience: [
    { label: "US Students", value: "36M+", icon: "graduation-cap" },
    { label: "US Universities", value: "4,000+", icon: "building" },
    { label: "US Companies", value: "6M+", icon: "briefcase" }
  ]
};

export const competitiveData = {
  features: [
    "Personalized Roadmap",
    "Career Clarity",
    "Affordable & Scalable",
    "AI Coaching",
    "Understands You",
    "On Demand",
    "Predictive Trends"
  ],
  competitors: [
    {
      name: "CASEY",
      scores: ["yes", "yes", "yes", "yes", "yes", "yes", "yes"],
      color: "#10B981",
      highlight: true
    },
    {
      name: "LinkedIn",
      scores: ["no", "no", "yes", "no", "no", "yes", "no"],
      color: "#0A66C2"
    },
    {
      name: "Teal",
      scores: ["yes", "no", "partial", "yes", "partial", "yes", "no"],
      color: "#64748B"
    },
    {
      name: "Career Coach",
      scores: ["yes", "partial", "no", "partial", "partial", "no", "no"],
      color: "#64748B"
    }
  ]
};

export const b2bCompetitiveData = {
  features: [
    "Dynamic Profile",
    "Living Identity",
    "Predictive Fit",
    "Motivation & Work-Style Signals",
    "Hiring & Mobility Intelligence",
    "Private Alignment",
    "ATS Integrations"
  ],
  competitors: [
    {
      name: "CASEY",
      label: "Us",
      scores: ["yes", "yes", "yes", "yes", "yes", "yes", "yes"],
      highlight: true
    },
    {
      name: "ATS / HRIS",
      subtitle: "Workday, Greenhouse",
      scores: ["no", "no", "partial", "no", "partial", "no", "yes"]
    },
    {
      name: "AI Screening",
      subtitle: "HireVue, Sapia",
      scores: ["partial", "no", "partial", "partial", "no", "no", "partial"]
    },
    {
      name: "Talent Marketplaces",
      subtitle: "Workable, Mercor",
      scores: ["no", "no", "partial", "no", "no", "no", "partial"]
    },
    {
      name: "Assessments",
      subtitle: "Hogan, SHL",
      scores: ["partial", "no", "partial", "partial", "no", "no", "no"]
    }
  ]
};

export const moatData = [
  {
    title: "Clarity Engine",
    description: "Every interaction compounds what CASEY understands about a person.",
    icon: "refresh-cw"
  },
  {
    title: "Career Intelligence Layer",
    description: "Built on living profiles, not resumes.",
    icon: "cpu"
  },
  {
    title: "Community-Driven Stickiness",
    description: "Students return and engage regularly.",
    icon: "users"
  },
  {
    title: "University & Employer Integrations",
    description: "Intelligence used inside education and hiring systems.",
    icon: "link"
  }
];

export const businessModel = {
  totalRevenue: "$100M ARR",
  channels: [
    {
      name: "B2B Recruitment Intelligence",
      percentage: 80,
      description: "Enterprise subscriptions, candidate matching intelligence, ATS integrations",
      color: "#0EA5E9"
    },
    {
      name: "B2U Institutional Licensing",
      percentage: 15,
      description: "Universities, workforce programs, youth organizations",
      color: "#10B981"
    },
    {
      name: "B2C Premium & Affiliates",
      percentage: 5,
      description: "Paid upgrades, upskilling platform partnerships",
      color: "#8B5CF6"
    }
  ]
};

export const roadmapData = [
  {
    year: "2025",
    quarter: "Q4",
    title: "MVP Development",
    description: "Complete MVP development and core platform features",
    milestones: ["Clarity MVP", "Alpha Launch", "Core Features"],
    status: "completed"
  },
  {
    year: "2026",
    quarter: "Q1-Q2",
    title: "Beta Launch & Validation (Now)",
    description: "User acquisition, university pilots, early traction",
    milestones: ["User Acquisition", "University Pilots", "Early Traction"],
    status: "current"
  },
  {
    year: "2026",
    quarter: "Q3-Q4",
    title: "Revenue Growth",
    description: "Employer partnerships, B2B testing, CASEY Pro launch",
    milestones: ["100K B2C Users", "Employer Partnerships", "HR Recruitment (B2B) Testing", "CASEY Pro Launch"],
    status: "upcoming"
  },
  {
    year: "2027",
    quarter: "Full Year",
    title: "Enterprise Scale",
    description: "Enterprise adoption, national expansion",
    milestones: ["B2B Maturity", "Seed Round", "International Interest"],
    status: "future"
  },
  {
    year: "2028",
    quarter: "Full Year",
    title: "$100M ARR",
    description: "Category leadership, global expansion",
    milestones: ["250+ Institutions", "1,000+ Employers", "Global Expansion"],
    status: "future"
  }
];

export const financialProjections = {
  targetARR: "$100M",
  targetYear: "2028",
  phases: [
    {
      year: "2026",
      label: "Signal Creation",
      focus: "User mass, engagement validation",
      revenue: "Limited pilots"
    },
    {
      year: "2027",
      label: "Institutional Scale",
      focus: "B2U expansion, employer entry",
      revenue: "Growing ARR"
    },
    {
      year: "2028",
      label: "Enterprise Acceleration",
      focus: "Category leadership",
      revenue: "$100M ARR"
    }
  ],
  drivers: [
    "User density creates proprietary intelligence",
    "Institutional leverage adds users and recurring revenue",
    "Enterprise monetization enables premium pricing",
    "Longitudinal relationships increase lifetime value",
    "Network effects compound growth"
  ]
};

export const teamMembers = [
  {
    name: "Maaz Kazi",
    role: "CEO & CTO",
    bio: "2nd-time founder with a computer science background, building AI-driven consumer products",
    image: "https://customer-assets.emergentagent.com/job_04e4e328-1ca5-412a-9b8c-82af7948c90e/artifacts/ti77qzxy_Maaz%20Profile%20Pic.jpg",
    linkedin: "https://www.linkedin.com/in/maazkazi26/",
    type: "founder"
  },
  {
    name: "Victor Isakov",
    role: "CFO",
    bio: "Finance and strategy lead with expertise in data-driven growth, financial modeling, and $15M+ portfolios",
    image: "https://customer-assets.emergentagent.com/job_04e4e328-1ca5-412a-9b8c-82af7948c90e/artifacts/ncw33jbh_Portrait%20-%20Victor_6.png",
    linkedin: "https://www.linkedin.com/in/victor-isakov/",
    type: "founder"
  },
  {
    name: "Dylan Bryan",
    role: "CMO & Head of Growth",
    bio: "Performance and growth leader coaching 120+ athletes and developing applied behavior-change systems",
    image: "https://customer-assets.emergentagent.com/job_invest-caseyai/artifacts/7zv6coos_Dylan%202.jpeg",
    linkedin: "https://www.linkedin.com/in/dylan-bryan-069aaa286/",
    type: "team"
  },
  {
    name: "Audrey R. Hametner",
    role: "Board Director & Advisor",
    bio: "Education and workforce innovation leader advising Fortune 500 companies and universities for 26+ years",
    image: "https://customer-assets.emergentagent.com/job_04e4e328-1ca5-412a-9b8c-82af7948c90e/artifacts/5ku375d0_Audrey%20R.jpeg",
    linkedin: "https://www.linkedin.com/in/audreyhametner/",
    type: "advisor"
  }
];

export const investmentAsk = {
  amount: "$1M",
  round: "Pre-Seed",
  raised: "$30K",
  raisedNote: "Already raised",
  valuation: "$10M Post-Money Valuation",
  allocation: [
    {
      category: "Product Development",
      percentage: 50,
      amount: "$500K",
      description: "Complete core clarity infrastructure and enterprise-ready architecture",
      color: "#0EA5E9"
    },
    {
      category: "Sales & Marketing",
      percentage: 35,
      amount: "$350K",
      description: "B2C acquisition, campus activation, institutional pilots",
      color: "#10B981"
    },
    {
      category: "Research",
      percentage: 15,
      amount: "$150K",
      description: "Behavioral science frameworks, expert advisory, validation",
      color: "#8B5CF6"
    }
  ],
  goals: [
    { metric: "100,000+", label: "Users in first year" },
    { metric: "1,000+", label: "University & business partners" },
    { metric: "AI Model", label: "Refined on real user data" }
  ]
};

export const testimonials = [
  {
    quote: "It gave me direction instead of endless tips and random resources.",
    name: "Alex Carter",
    role: "Data Analyst",
    image: "https://framerusercontent.com/images/smBtdyJjxMOiX8qlXLowR4kih8.jpeg?width=640&height=445"
  },
  {
    quote: "The step by step structure made it easier to stay focused and keep going.",
    name: "Priya Mehta",
    role: "Mid-Level Designer",
    image: "https://framerusercontent.com/images/94kzGZvBIAxtoOwhtMd3TIlJYEs.jpeg?width=640&height=529"
  },
  {
    quote: "Everything feels aligned with real roles and real expectations.",
    name: "Marcus Reed",
    role: "Senior UX Designer",
    image: "https://framerusercontent.com/images/MUeZo44APUwzEbSgfPSPJ2ARgxk.jpeg?width=640&height=640"
  }
];
